import React, { useState, useEffect } from 'react';
import CustomModal from '../reusable/CustomModal';
import CustomButton from '../reusable/CustomButton';
import { executeSchoolReportWorkflowTransition, WorkflowTransitionResponse } from '../../services/workflowService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import { USER_ROLES } from '../../constants';

interface ApproveSchoolReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transitionResponse: WorkflowTransitionResponse) => void;
  schoolId: string;
  currentStatus: string;
}

const ApproveSchoolReportModal = ({
  isOpen,
  onClose,
  onSuccess,
  schoolId,
  currentStatus,
}: ApproveSchoolReportModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen } = useErrorModal();

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsLoading(false);
    onClose();
  };

  const handleApprove = async () => {
    if (!userDetails || !userDetails.eoaId || !userDetails.access_token) {
      handleOpen('User details not available.', new Error('User details not available.'));
      return;
    }

    setIsLoading(true);
    try {
      let transitionName: string;

      if (currentStatus === 'SCHOOL_REPORT_READY_FOR_DISTRICT_REVIEW' && userDetails.role === USER_ROLES.XQ_ADMIN) {
        transitionName = 'finalize_school_report';
      } else if (userDetails.role === USER_ROLES.LEAD_FACILITATOR) {
        transitionName = 'lead_facilitator_approve_school_report';
      } else {
        transitionName = 'approve_school_report';
      }

      const transitionResponse = await executeSchoolReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName
      );

      onSuccess(transitionResponse);
      handleClose();
    } catch (error) {
      handleOpen('Error occurred while approving school report', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMessage = () => {
    if (userDetails?.role === USER_ROLES.XQ_ADMIN) {
      if (currentStatus === 'SCHOOL_REPORT_READY_FOR_DISTRICT_REVIEW') {
        return 'By confirming, you are indicating that you have reviewed the school report with all relevant <b>district</b> staff and they have approved the content.';
      }
      if (currentStatus === 'SCHOOL_REPORT_READY_FOR_INTERNAL_REVIEW') {
        return 'The Lead Facilitator has <b>not</b> approved the school report yet.<br/><br/>By confirming, you will approve the school report content, and the Lead Facilitator will no longer have the option to approve or reject it.';
      }
    }
    return 'By confirming, you are <b>approving</b> the school report.';
  };

  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose} title="Are you sure?" icon={false} maxWidth="sm">
      <p className="approve-data-qc-description" dangerouslySetInnerHTML={{ __html: getMessage() }} />
      <div className="confirmation-buttons">
        <CustomButton variant="secondary" buttonText="Cancel" buttonAction={handleClose} disabled={isLoading} />
        <CustomButton variant="primary" buttonText="Confirm" buttonAction={handleApprove} loading={isLoading} />
      </div>
    </CustomModal>
  );
};

export default ApproveSchoolReportModal;
